import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../components/Auth/AuthProvider'

type ReAuthPromptProps = {
  open: boolean
  onClose: () => void
  message?: string
  maxAgeMinutes?: number
}

export const ReAuthPrompt: React.FC<ReAuthPromptProps> = ({ open, onClose, message, maxAgeMinutes }) => {
  const theme = useTheme()
  const { user } = useAuth()
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  if (!open) {
    return null
  }

  const handleReAuth = async () => {
    if (!user?.email) {
      setError('Your session email is unavailable. Please sign out and sign back in.')
      setStatus('error')
      return
    }

    try {
      setStatus('sending')
      setError(null)
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: user.email,
        options: {
          emailRedirectTo: window.location.href,
        },
      })

      if (otpError) {
        setError(otpError.message)
        setStatus('error')
        return
      }

      setStatus('sent')
    } catch (err: any) {
      setError(err?.message || 'Failed to start re-authentication')
      setStatus('error')
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 8,
          maxWidth: 420,
          width: '100%',
          padding: 24,
          boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>Recent Authentication Required</h3>
        <p style={{ fontSize: 14, color: theme.palette.text.secondary }}>
          {message || 'For security, this action requires you to have authenticated within the last 30 minutes.'}
        </p>
        {maxAgeMinutes && (
          <p style={{ fontSize: 13, color: theme.palette.text.secondary }}>
            Allowed session age: {maxAgeMinutes} minutes
          </p>
        )}
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 6,
            background: theme.palette.mode === 'dark' ? 'rgba(33,150,243,0.1)' : '#e3f2fd',
            border: '1px solid rgba(33,150,243,0.3)',
            fontSize: 13,
          }}
        >
          {!user?.email && <strong>We could not determine your email address for re-authentication.</strong>}
          {user?.email && (
            <>
              <strong>We will email a fresh login link to {user.email}.</strong>
              <br />
              Follow the link to re-authenticate, then return to this page to retry the action.
            </>
          )}
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 6,
              background: theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.2)' : '#fdecea',
              border: '1px solid rgba(211,47,47,0.4)',
              color: theme.palette.mode === 'dark' ? '#ffb4a9' : '#b71c1c',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {status === 'sent' && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 6,
              background: theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.2)' : '#e8f5e9',
              border: '1px solid rgba(76,175,80,0.4)',
              color: theme.palette.mode === 'dark' ? '#b9f6ca' : '#1b5e20',
              fontSize: 13,
            }}
          >
            Magic link sent. Check your email to complete re-authentication, then retry the action.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5',
              color: theme.palette.text.primary,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleReAuth}
            disabled={status === 'sending'}
            style={{
              padding: '8px 16px',
              borderRadius: 4,
              border: 'none',
              background: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              opacity: status === 'sending' ? 0.7 : 1,
            }}
          >
            {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Resend Link' : 'Send Re-Auth Link'}
          </button>
        </div>
      </div>
    </div>
  )
}
